/*
 * ProcessCommissionsTab — admin-side controller for the affiliate payout
 * engine. Consumed by /plog/commission_ledger_v2 (Process tab).
 *
 * Sections (top → bottom):
 *   1. Status cards (Sandbox/Live, manual-run flag, eligible balance, Stripe-ready affiliates)
 *   2. "PROCESS ALL AVAILABLE COMMISSIONS" CTA (typed confirmation in live mode)
 *   3. On Hold table (FROZEN + HIGH_VALUE_HOLD) with Guardian trust/threat columns
 *   4. Webhook Health card + Reprocess / Resolve controls
 *   5. Recent Batches list with per-batch retry + "Retry ALL failed" bulk action
 */
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  AlertTriangle, CheckCircle2, RefreshCw, Play, ShieldAlert, Shield,
  Wallet, ExternalLink, ChevronDown, ChevronRight, CircleAlert,
} from "lucide-react";
import { authAxios } from "../../utils/auth";

const cents = (c) => (c == null ? "—" : `$${(Number(c) / 100).toFixed(2)}`);

// Guardian tier / status → pill colours (user-approved palette)
const GUARDIAN_PILL = {
  watch:       "bg-amber-500/15 text-amber-300 ring-amber-500/30",
  prewarn:     "bg-orange-500/15 text-orange-300 ring-orange-500/30",
  freeze:      "bg-red-500/15 text-red-300 ring-red-500/30",
  high_value:  "bg-purple-500/15 text-purple-300 ring-purple-500/30",
  cleared:     "bg-emerald-500/15 text-emerald-300 ring-emerald-500/30",
};

const HOLD_PILL = {
  FROZEN:           "bg-red-500/15 text-red-300 ring-red-500/30",
  HIGH_VALUE_HOLD:  "bg-purple-500/15 text-purple-300 ring-purple-500/30",
};

const BATCH_PILL = {
  SCHEDULED:  "bg-sky-500/15 text-sky-300 ring-sky-500/30",
  EXECUTING:  "bg-amber-500/15 text-amber-300 ring-amber-500/30",
  COMPLETED:  "bg-emerald-500/15 text-emerald-300 ring-emerald-500/30",
  PARTIAL:    "bg-orange-500/15 text-orange-300 ring-orange-500/30",
};

const trustColor = (score) => {
  if (score == null) return "text-white/40";
  if (score >= 80) return "text-emerald-300";
  if (score >= 50) return "text-amber-300";
  return "text-red-300";
};

export default function ProcessCommissionsTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmPhrase, setConfirmPhrase] = useState("");
  const [retrying, setRetrying] = useState(false);
  const [expandedBatch, setExpandedBatch] = useState(null);
  const [reprocessing, setReprocessing] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await authAxios.get("/admin/payouts/dashboard");
      setData(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load payouts dashboard");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (!data && loading) {
    return (
      <div className="text-white/40 text-sm py-12 text-center" data-testid="process-commissions-loading">
        <RefreshCw className="w-4 h-4 animate-spin inline-block mr-2" /> Loading payouts dashboard…
      </div>
    );
  }
  if (!data) return null;

  const isLive = data.config.stripe_mode === "live";
  const needsConfirmPhrase = isLive; // sandbox = plain confirm click
  const requiredPhrase = "PROCESS PAYOUTS";
  const canProcess =
    data.config.manual_run_enabled &&
    (data.preview?.eligible_count || 0) > 0 &&
    !running;

  const openConfirm = () => {
    setConfirmPhrase("");
    setShowConfirm(true);
  };

  const runBatch = async () => {
    setRunning(true);
    try {
      const res = await authAxios.post("/admin/payouts/run");
      const paid = res.data?.total_paid_cents || 0;
      const failed = res.data?.total_failed || 0;
      if (res.data?.batch_id) {
        toast.success(
          `Batch ${res.data.batch_id.slice(0, 20)} • ${cents(paid)} paid` +
          (failed ? ` • ${failed} failed` : ""),
        );
      } else {
        toast.info(res.data?.note || "Nothing to pay out");
      }
      setShowConfirm(false);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Payout run failed");
    } finally {
      setRunning(false);
    }
  };

  const retryAllFailed = async () => {
    setRetrying(true);
    try {
      const res = await authAxios.post("/admin/payouts/retry-all-failed");
      toast.success(`Retried ${res.data?.total_retried ?? 0} items across ${res.data?.batches_touched ?? 0} batches`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Retry-all failed");
    } finally {
      setRetrying(false);
    }
  };

  const retryOne = async (batchId) => {
    try {
      const res = await authAxios.post(`/admin/payouts/batches/${encodeURIComponent(batchId)}/retry`);
      toast.success(`Retry: ${res.data?.retried ?? 0} items`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Retry failed");
    }
  };

  const reprocessWebhook = async (eventId, action = "reprocess") => {
    setReprocessing((m) => ({ ...m, [eventId]: action }));
    try {
      const url = action === "resolve"
        ? `/admin/webhooks/${encodeURIComponent(eventId)}/resolve`
        : `/admin/webhooks/${encodeURIComponent(eventId)}/reprocess`;
      const res = await authAxios.post(url);
      toast.success(`${action === "resolve" ? "Resolved" : "Reprocessed"} • status: ${res.data?.status || "ok"}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || `${action} failed`);
    } finally {
      setReprocessing((m) => { const n = { ...m }; delete n[eventId]; return n; });
    }
  };

  const wh = data.webhook_health || {};
  const whFailed = wh.failed_last_7d || [];
  const whStuck = wh.stuck_processing || [];
  const has_wh_issues = wh.has_issues;

  return (
    <section data-testid="process-commissions-tab" className="space-y-5">
      {/* ────────────── Status Cards ────────────── */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="status-cards">
        <StatusCard
          title="Mode"
          value={isLive ? "LIVE" : "SANDBOX (test)"}
          tint={isLive ? "text-red-300 ring-red-500/30" : "text-amber-300 ring-amber-500/30"}
          hint={`Manual run: ${data.config.manual_run_enabled ? "enabled" : "disabled"}`}
        />
        <StatusCard
          title="Eligible Now"
          value={cents(data.preview?.total_cents || 0)}
          tint="text-emerald-300 ring-emerald-500/30"
          hint={`${data.preview?.eligible_count || 0} affiliate${(data.preview?.eligible_count || 0) === 1 ? "" : "s"} ready`}
        />
        <StatusCard
          title="Excluded"
          value={String(data.preview?.excluded_count || 0)}
          tint="text-white/60 ring-white/10"
          hint="Missing Stripe Connect / no balance"
        />
        <StatusCard
          title="On Hold"
          value={cents(data.on_hold_totals?.held_cents || 0)}
          tint="text-red-300 ring-red-500/30"
          hint={`${data.on_hold_totals?.user_count || 0} user${(data.on_hold_totals?.user_count || 0) === 1 ? "" : "s"} · FROZEN + HIGH_VALUE`}
        />
      </div>

      {/* ────────────── Big CTA ────────────── */}
      <div className="rounded-xl border border-[#C5A55A]/30 bg-gradient-to-br from-[#C5A55A]/10 to-transparent p-5 flex flex-col md:flex-row md:items-center justify-between gap-4" data-testid="process-cta-card">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            <Wallet className="w-4 h-4 text-[#C5A55A]" />
            <h3 className="text-sm font-semibold text-[#C5A55A]">Process All Available Commissions</h3>
          </div>
          <p className="text-xs text-white/60 leading-relaxed">
            Reserves every <span className="text-emerald-300 font-mono">AVAILABLE</span> row for eligible affiliates and fires a Stripe Transfer per user. Idempotent per batch — safe to retry.
            {!data.config.manual_run_enabled && (
              <span className="block text-amber-300 mt-1">
                <AlertTriangle className="w-3 h-3 inline-block mr-1" />
                Manual runs are disabled for the current mode. Flip `payout_manual_run_prod` to enable.
              </span>
            )}
          </p>
        </div>
        <button
          onClick={openConfirm}
          disabled={!canProcess}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-[#C5A55A] hover:bg-[#d4b56a] text-black font-semibold text-sm disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap"
          data-testid="process-all-btn"
        >
          <Play className="w-4 h-4" />
          {running ? "Processing…" : "Process All Available"}
        </button>
      </div>

      {/* ────────────── Confirmation Modal ────────────── */}
      {showConfirm && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
          onClick={() => !running && setShowConfirm(false)}
          data-testid="process-confirm-modal"
        >
          <div
            className="w-full max-w-md rounded-xl border border-[#C5A55A]/30 bg-[#111113] p-5 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-sm font-semibold text-[#C5A55A] mb-3 flex items-center gap-2">
              <ShieldAlert className="w-4 h-4" /> Confirm Payout Run
            </h3>
            <div className="text-[13px] text-white/70 space-y-2 mb-4">
              <p>You are about to transfer{" "}
                <span className="text-emerald-300 font-mono">{cents(data.preview.total_cents)}</span>
                {" "}across{" "}
                <span className="text-white font-mono">{data.preview.eligible_count}</span>
                {" "}affiliate{data.preview.eligible_count === 1 ? "" : "s"} via Stripe Connect.</p>
              <p className={`text-xs ${isLive ? "text-red-300" : "text-amber-300"}`}>
                Mode: <span className="font-mono">{data.config.stripe_mode.toUpperCase()}</span>
                {isLive ? " — real money will move." : " — Stripe test-mode transfers only."}
              </p>
            </div>

            {needsConfirmPhrase && (
              <div className="mb-4">
                <label className="text-[11px] uppercase tracking-wider text-white/40 block mb-1">
                  Type <span className="font-mono text-[#C5A55A]">{requiredPhrase}</span> to confirm
                </label>
                <input
                  type="text"
                  value={confirmPhrase}
                  onChange={(e) => setConfirmPhrase(e.target.value)}
                  className="w-full bg-[#0a0a0a] border border-white/10 rounded px-3 py-2 text-sm text-white font-mono"
                  placeholder={requiredPhrase}
                  autoFocus
                  data-testid="process-confirm-input"
                />
              </div>
            )}

            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowConfirm(false)}
                disabled={running}
                className="px-3 py-2 text-xs rounded-lg bg-white/5 hover:bg-white/10 text-white/70 disabled:opacity-40"
                data-testid="process-cancel-btn"
              >
                Cancel
              </button>
              <button
                onClick={runBatch}
                disabled={running || (needsConfirmPhrase && confirmPhrase !== requiredPhrase)}
                className="px-4 py-2 text-xs rounded-lg bg-[#C5A55A] hover:bg-[#d4b56a] text-black font-semibold disabled:opacity-40"
                data-testid="process-confirm-btn"
              >
                {running ? "Processing…" : "Fire Payouts"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ────────────── On Hold table ────────────── */}
      <section
        className="rounded-xl border border-white/[0.06] bg-[#111113] overflow-hidden"
        data-testid="on-hold-section"
      >
        <header className="flex items-center justify-between px-4 py-3 border-b border-white/10">
          <div className="flex items-center gap-2">
            <Shield className="w-4 h-4 text-red-300" />
            <h3 className="text-sm font-semibold text-white/80">On Hold</h3>
            <span className="text-[11px] text-white/40">
              {data.on_hold?.length || 0} affiliate{(data.on_hold?.length || 0) === 1 ? "" : "s"} · {cents(data.on_hold_totals?.held_cents || 0)}
            </span>
          </div>
          <button
            onClick={load}
            disabled={loading}
            className="text-[11px] text-white/40 hover:text-white flex items-center gap-1"
            data-testid="on-hold-refresh"
          >
            <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
        </header>
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="on-hold-table">
            <thead className="bg-white/[0.03] text-white/40 uppercase tracking-wider text-[10px]">
              <tr>
                <th className="text-left py-2 px-3">Affiliate</th>
                <th className="text-left py-2 px-3">Hold Reason</th>
                <th className="text-right py-2 px-3">Held</th>
                <th className="text-right py-2 px-3">Rows</th>
                <th className="text-left py-2 px-3">Guardian Tier</th>
                <th className="text-left py-2 px-3">Threat Class</th>
                <th className="text-right py-2 px-3">Trust</th>
                <th className="text-right py-2 px-3">Risk</th>
                <th className="text-left py-2 px-3">Action</th>
              </tr>
            </thead>
            <tbody>
              {(data.on_hold || []).length === 0 && (
                <tr><td colSpan="9" className="text-center py-8 text-white/30">Nothing on hold</td></tr>
              )}
              {(data.on_hold || []).map((r) => (
                <tr key={`${r.user_id}-${r.hold_reason}`} className="border-t border-white/[0.04] hover:bg-white/[0.02]"
                    data-testid={`on-hold-row-${r.user_id}`}>
                  <td className="py-1.5 px-3">
                    <div className="text-white/80">{r.name || "—"}</div>
                    <div className="text-[10px] font-mono text-white/40">{r.user_id}</div>
                  </td>
                  <td className="py-1.5 px-3">
                    <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] ring-1 ${HOLD_PILL[r.hold_reason] || ""}`}>
                      {r.hold_reason?.replace("_HOLD", "")}
                    </span>
                  </td>
                  <td className="py-1.5 px-3 text-right font-mono text-red-300">{cents(r.held_cents)}</td>
                  <td className="py-1.5 px-3 text-right text-white/60">{r.row_count}</td>
                  <td className="py-1.5 px-3">
                    {r.guardian_tier ? (
                      <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] ring-1 ${GUARDIAN_PILL[r.guardian_tier] || ""}`}>
                        {r.guardian_tier}
                      </span>
                    ) : <span className="text-white/30">—</span>}
                  </td>
                  <td className="py-1.5 px-3 text-white/60">{r.threat_class || "—"}</td>
                  <td className={`py-1.5 px-3 text-right font-mono ${trustColor(r.trust_score)}`}>{r.trust_score ?? "—"}</td>
                  <td className="py-1.5 px-3 text-right font-mono text-white/60">{r.risk_score ?? "—"}</td>
                  <td className="py-1.5 px-3">
                    {r.guardian_case_id ? (
                      <a
                        href={`/admin/analytics?tab=guardian&case=${encodeURIComponent(r.guardian_case_id)}`}
                        className="text-[#C5A55A] hover:underline inline-flex items-center gap-1"
                        data-testid={`open-case-${r.user_id}`}
                      >
                        Open Case <ExternalLink className="w-3 h-3" />
                      </a>
                    ) : <span className="text-white/30 text-[11px]">no open case</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* ────────────── Webhook Health ────────────── */}
      <section
        className={`rounded-xl border ${has_wh_issues ? "border-amber-500/30 bg-amber-500/[0.03]" : "border-white/[0.06] bg-[#111113]"} overflow-hidden`}
        data-testid="webhook-health-section"
      >
        <header className="flex items-center justify-between px-4 py-3 border-b border-white/10">
          <div className="flex items-center gap-2">
            {has_wh_issues ? (
              <AlertTriangle className="w-4 h-4 text-amber-300" />
            ) : (
              <CheckCircle2 className="w-4 h-4 text-emerald-300" />
            )}
            <h3 className="text-sm font-semibold text-white/80">Webhook Health</h3>
          </div>
          <div className="text-[11px] text-white/40 font-mono">
            {Object.entries(wh.last_24h_by_status || {}).map(([s, n]) => (
              <span key={s} className="mr-3">{s}: {n}</span>
            ))}
          </div>
        </header>

        {!has_wh_issues && (
          <div className="px-4 py-6 text-center text-emerald-300 text-xs" data-testid="webhook-clean">
            <CheckCircle2 className="w-5 h-5 inline-block mr-1" /> No webhook issues in the last 7 days.
          </div>
        )}

        {(whFailed.length > 0 || whStuck.length > 0) && (
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="webhook-issues-table">
              <thead className="bg-white/[0.03] text-white/40 uppercase tracking-wider text-[10px]">
                <tr>
                  <th className="text-left py-2 px-3">Event ID</th>
                  <th className="text-left py-2 px-3">Type</th>
                  <th className="text-left py-2 px-3">Status</th>
                  <th className="text-left py-2 px-3">Received</th>
                  <th className="text-left py-2 px-3">Error</th>
                  <th className="text-right py-2 px-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {whFailed.map((e) => (
                  <WebhookRow key={e.stripe_event_id} evt={e} status="failed"
                    onAction={reprocessWebhook} busy={reprocessing[e.stripe_event_id]} />
                ))}
                {whStuck.map((e) => (
                  <WebhookRow key={e.stripe_event_id} evt={e} status="stuck"
                    onAction={reprocessWebhook} busy={reprocessing[e.stripe_event_id]} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* ────────────── Recent Batches ────────────── */}
      <section
        className="rounded-xl border border-white/[0.06] bg-[#111113] overflow-hidden"
        data-testid="recent-batches-section"
      >
        <header className="flex items-center justify-between px-4 py-3 border-b border-white/10">
          <div className="flex items-center gap-2">
            <RefreshCw className="w-4 h-4 text-[#C5A55A]" />
            <h3 className="text-sm font-semibold text-white/80">Recent Batches</h3>
            <span className="text-[11px] text-white/40">
              {(data.recent_batches || []).length} shown
            </span>
          </div>
          {data.partial_batch_count > 0 && (
            <button
              onClick={retryAllFailed}
              disabled={retrying}
              className="text-[11px] px-3 py-1.5 rounded-lg bg-orange-500/10 hover:bg-orange-500/20 border border-orange-500/30 text-orange-300 disabled:opacity-40"
              data-testid="retry-all-failed-btn"
            >
              <RefreshCw className={`w-3 h-3 inline-block mr-1 ${retrying ? "animate-spin" : ""}`} />
              Retry ALL Failed ({data.partial_batch_count} batches)
            </button>
          )}
        </header>
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="batches-table">
            <thead className="bg-white/[0.03] text-white/40 uppercase tracking-wider text-[10px]">
              <tr>
                <th className="w-8"></th>
                <th className="text-left py-2 px-3">Batch ID</th>
                <th className="text-left py-2 px-3">Created</th>
                <th className="text-left py-2 px-3">Status</th>
                <th className="text-right py-2 px-3">Items</th>
                <th className="text-right py-2 px-3">Reserved</th>
                <th className="text-right py-2 px-3">Paid</th>
                <th className="text-left py-2 px-3">Executed</th>
                <th className="text-right py-2 px-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {(data.recent_batches || []).length === 0 ? (
                <tr><td colSpan="9" className="text-center py-8 text-white/30">No payout batches yet</td></tr>
              ) : (data.recent_batches || []).map((b) => {
                const isOpen = expandedBatch === b.id;
                return [
                  <tr key={b.id} className="border-t border-white/[0.04] hover:bg-white/[0.02]"
                      data-testid={`batch-row-${b.id}`}>
                    <td className="py-1.5 px-3">
                      <button
                        onClick={() => setExpandedBatch(isOpen ? null : b.id)}
                        className="text-white/40 hover:text-white"
                        data-testid={`batch-expand-${b.id}`}
                      >
                        {isOpen ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                      </button>
                    </td>
                    <td className="py-1.5 px-3 font-mono text-[10px] text-white/60">{b.id}</td>
                    <td className="py-1.5 px-3 font-mono text-[10px] text-white/40">
                      {b.created_at ? new Date(b.created_at).toLocaleString() : "—"}
                    </td>
                    <td className="py-1.5 px-3">
                      <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] ring-1 ${BATCH_PILL[b.status] || "ring-white/10"}`}>
                        {b.status}
                      </span>
                      {b.is_sandbox && <span className="ml-1 text-[9px] text-amber-300">SBX</span>}
                    </td>
                    <td className="py-1.5 px-3 text-right text-white/60">{(b.items || []).length}</td>
                    <td className="py-1.5 px-3 text-right font-mono text-white/80">{cents(b.total_reserved_cents)}</td>
                    <td className="py-1.5 px-3 text-right font-mono text-emerald-300">{cents(b.total_paid_cents)}</td>
                    <td className="py-1.5 px-3 font-mono text-[10px] text-white/40">
                      {b.executed_at ? new Date(b.executed_at).toLocaleString() : "—"}
                    </td>
                    <td className="py-1.5 px-3 text-right">
                      {(b.status === "PARTIAL" || b.status === "SCHEDULED") && (
                        <button
                          onClick={() => retryOne(b.id)}
                          className="text-[10px] px-2 py-1 rounded bg-orange-500/10 hover:bg-orange-500/20 text-orange-300 border border-orange-500/30"
                          data-testid={`batch-retry-${b.id}`}
                        >
                          Retry
                        </button>
                      )}
                    </td>
                  </tr>,
                  isOpen && (
                    <tr key={`${b.id}-details`} className="bg-black/40">
                      <td colSpan="9" className="p-4">
                        <div className="text-[11px] text-white/60 mb-2 font-medium">Batch items ({(b.items || []).length})</div>
                        <div className="overflow-x-auto">
                          <table className="w-full text-[11px]" data-testid={`batch-items-${b.id}`}>
                            <thead className="text-white/40 uppercase tracking-wider text-[9px]">
                              <tr>
                                <th className="text-left py-1 px-2">User</th>
                                <th className="text-left py-1 px-2">Email</th>
                                <th className="text-right py-1 px-2">Reserved</th>
                                <th className="text-left py-1 px-2">Status</th>
                                <th className="text-left py-1 px-2">Transfer</th>
                                <th className="text-left py-1 px-2">Error</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(b.items || []).map((it) => (
                                <tr key={it.user_id} className="border-t border-white/[0.04]">
                                  <td className="py-1 px-2 font-mono text-[10px] text-white/60">{it.user_id}</td>
                                  <td className="py-1 px-2 text-white/60">{it.email || "—"}</td>
                                  <td className="py-1 px-2 text-right font-mono text-white/80">{cents(it.reserved_cents)}</td>
                                  <td className="py-1 px-2">
                                    <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] ring-1 ${
                                      it.status === "PAID" ? "bg-emerald-500/15 text-emerald-300 ring-emerald-500/30"
                                      : it.status === "FAILED" ? "bg-red-500/15 text-red-300 ring-red-500/30"
                                      : "bg-sky-500/15 text-sky-300 ring-sky-500/30"
                                    }`}>{it.status}</span>
                                  </td>
                                  <td className="py-1 px-2 font-mono text-[10px] text-white/40">{it.stripe_transfer_id || "—"}</td>
                                  <td className="py-1 px-2 text-red-300 text-[10px]">{it.error || "—"}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </td>
                    </tr>
                  ),
                ];
              })}
            </tbody>
          </table>
        </div>
      </section>
    </section>
  );
}

function StatusCard({ title, value, tint, hint }) {
  return (
    <div className={`rounded-lg border ring-1 p-3 bg-[#111113] ring-white/10 ${tint || ""}`} data-testid={`status-card-${title.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="text-[10px] uppercase tracking-wider opacity-80">{title}</div>
      <div className="text-base font-semibold font-mono mt-1">{value}</div>
      {hint && <div className="text-[10px] text-white/50 mt-0.5">{hint}</div>}
    </div>
  );
}

function WebhookRow({ evt, status, onAction, busy }) {
  return (
    <tr className="border-t border-white/[0.04] hover:bg-white/[0.02]"
        data-testid={`webhook-row-${evt.stripe_event_id}`}>
      <td className="py-1.5 px-3 font-mono text-[10px] text-white/60">{evt.stripe_event_id}</td>
      <td className="py-1.5 px-3 text-white/60">{evt.event_type || "—"}</td>
      <td className="py-1.5 px-3">
        <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] ring-1 ${
          status === "failed" ? "bg-red-500/15 text-red-300 ring-red-500/30" : "bg-amber-500/15 text-amber-300 ring-amber-500/30"
        }`}>
          <CircleAlert className="w-3 h-3" /> {status}
        </span>
      </td>
      <td className="py-1.5 px-3 font-mono text-[10px] text-white/40">
        {evt.received_at ? new Date(evt.received_at).toLocaleString() : "—"}
      </td>
      <td className="py-1.5 px-3 text-red-300 text-[10px]">{evt.error || "—"}</td>
      <td className="py-1.5 px-3 text-right whitespace-nowrap">
        <button
          onClick={() => onAction(evt.stripe_event_id, "reprocess")}
          disabled={!!busy}
          className="text-[10px] px-2 py-1 rounded bg-[#C5A55A]/10 hover:bg-[#C5A55A]/20 text-[#C5A55A] border border-[#C5A55A]/30 disabled:opacity-40 mr-1"
          data-testid={`webhook-reprocess-${evt.stripe_event_id}`}
        >
          {busy === "reprocess" ? "…" : "Reprocess"}
        </button>
        <button
          onClick={() => onAction(evt.stripe_event_id, "resolve")}
          disabled={!!busy}
          className="text-[10px] px-2 py-1 rounded bg-white/5 hover:bg-white/10 text-white/60 border border-white/10 disabled:opacity-40"
          data-testid={`webhook-resolve-${evt.stripe_event_id}`}
          title="Mark as processed without re-running"
        >
          {busy === "resolve" ? "…" : "Resolve"}
        </button>
      </td>
    </tr>
  );
}
